import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FaLock, FaEye, FaEyeSlash, FaCheck, FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import './Auth.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [token, setToken] = useState('');
  const [userId, setUserId] = useState('');

  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const password = watch('password');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    const userIdParam = searchParams.get('user');
    
    if (tokenParam && userIdParam) {
      setToken(tokenParam);
      setUserId(userIdParam);
    } else {
      toast.error('Invalid or missing reset token');
      navigate('/forgot-password');
    }
  }, [searchParams, navigate]);

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch('/wp-json/xpunters/v1/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          token: token,
          new_password: data.password,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setResetSuccess(true);
        toast.success('Password reset successfully!');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        toast.error(result.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Reset password error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (resetSuccess) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card success-card">
            <div className="success-icon">
              <FaCheck />
            </div>
            <h2>Password Reset Successful!</h2>
            <p>Your password has been reset successfully.</p>
            <p className="success-hint">You will be redirected to login shortly...</p>
            <Link to="/login" className="btn btn-primary">
              Login Now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Link to="/login" className="back-to-login">
              <FaArrowLeft /> Back to Login
            </Link>
            <div className="auth-logo">
              <span className="logo-icon">🔐</span>
              <h1>Reset Password</h1>
            </div>
            <p className="auth-subtitle">
              Enter your new password below.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
            {/* New Password */}
            <div className="form-group">
              <label>New Password *</label>
              <div className="input-wrapper">
                <FaLock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password (min. 8 characters)"
                  {...register('password', { 
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' }
                  })}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.password && (
                <span className="error-message">{errors.password.message}</span>
              )}
              <div className="password-hint">
                <span className={password?.length >= 8 ? 'valid' : ''}>
                  {password?.length >= 8 ? '✅' : '○'} At least 8 characters
                </span>
                <span className={/[A-Z]/.test(password || '') ? 'valid' : ''}>
                  {/[A-Z]/.test(password || '') ? '✅' : '○'} Uppercase letter
                </span>
                <span className={/[0-9]/.test(password || '') ? 'valid' : ''}>
                  {/[0-9]/.test(password || '') ? '✅' : '○'} Number
                </span>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="form-group">
              <label>Confirm Password *</label>
              <div className="input-wrapper">
                <FaLock className="input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your new password"
                  {...register('confirmPassword', { 
                    required: 'Please confirm your password',
                    validate: value => value === password || 'Passwords do not match'
                  })}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword.message}</span>
              )}
            </div>

            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? <LoadingSpinner size="small" /> : 'Reset Password'}
            </button>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <p className="auth-footer">
              Remember your password? <Link to="/login">Login</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;